/**
 * Cipher Toast System - Premium Notifications
 * Stackable, animated notifications with multiple types
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  X
} from 'lucide-react'
import clsx from 'clsx'
import './ToastSystem.css'

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  dismissible?: boolean
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  updateToast: (id: string, updates: Partial<Omit<Toast, 'id'>>) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastId = 0
function generateId() {
  return `toast-${++toastId}-${Date.now()}`
}

const TOAST_ICONS: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  loading: Loader2,
}

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
  loading: 0, // Loading toasts don't auto-dismiss
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = generateId()
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? DEFAULT_DURATIONS[toast.type],
      dismissible: toast.dismissible ?? toast.type !== 'loading',
    }
    setToasts(prev => [...prev, newToast])
    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const updateToast = useCallback((id: string, updates: Partial<Omit<Toast, 'id'>>) => {
    setToasts(prev => prev.map(t =>
      t.id === id ? { ...t, ...updates } : t
    ))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  const { addToast, removeToast, updateToast } = context

  return {
    toast: addToast,
    dismiss: removeToast,
    update: updateToast,
    success: (title: string, message?: string) =>
      addToast({ type: 'success', title, message }),
    error: (title: string, message?: string) =>
      addToast({ type: 'error', title, message }),
    warning: (title: string, message?: string) =>
      addToast({ type: 'warning', title, message }),
    info: (title: string, message?: string) =>
      addToast({ type: 'info', title, message }),
    loading: (title: string, message?: string) =>
      addToast({ type: 'loading', title, message }),
    promise: async <T,>(
      promise: Promise<T>,
      options: {
        loading: string
        success: string | ((data: T) => string)
        error: string | ((err: Error) => string)
      }
    ): Promise<T> => {
      const id = addToast({ type: 'loading', title: options.loading })
      try {
        const result = await promise
        updateToast(id, {
          type: 'success',
          title: typeof options.success === 'function'
            ? options.success(result)
            : options.success,
          dismissible: true,
          duration: DEFAULT_DURATIONS.success,
        })
        return result
      } catch (err) {
        updateToast(id, {
          type: 'error',
          title: typeof options.error === 'function'
            ? options.error(err as Error)
            : options.error,
          dismissible: true,
          duration: DEFAULT_DURATIONS.error,
        })
        throw err
      }
    },
  }
}

function ToastContainer() {
  const context = useContext(ToastContext)
  if (!context) return null

  const { toasts, removeToast } = context

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="toast-container" role="region" aria-label="Notifications">
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </div>,
    document.body
  )
}

interface ToastItemProps {
  toast: Toast
  onDismiss: () => void
  index: number
}

function ToastItem({ toast, onDismiss, index }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false)
  const Icon = TOAST_ICONS[toast.type]

  const handleDismiss = useCallback(() => {
    setIsExiting(true)
    setTimeout(onDismiss, 200)
  }, [onDismiss])

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(handleDismiss, toast.duration)
      return () => clearTimeout(timer)
    }
  }, [toast.duration, handleDismiss])

  return (
    <div
      className={clsx(
        'toast-item',
        `toast-${toast.type}`,
        isExiting && 'toast-exiting'
      )}
      style={{ '--toast-index': index } as React.CSSProperties}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className={clsx('toast-icon', toast.type === 'loading' && 'toast-icon-spin')}>
        <Icon size={20} />
      </div>

      <div className="toast-content">
        <div className="toast-title">{toast.title}</div>
        {toast.message && (
          <div className="toast-message">{toast.message}</div>
        )}
      </div>

      {toast.dismissible && (
        <button
          className="toast-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      )}

      {toast.duration && toast.duration > 0 && (
        <div
          className="toast-progress"
          style={{ '--duration': `${toast.duration}ms` } as React.CSSProperties}
        />
      )}
    </div>
  )
}

export default ToastProvider
